import * as React from 'react';
import { observer } from 'mobx-react';
import { Input, InputProps, Label, FormFeedback } from 'reactstrap';
import { InputType } from 'reactstrap/lib/Input';

import { KeyboardCodes } from "@app/AppConstants";
import { BaseFormModel } from '@app/Services/BaseFormModel';
import { OptionsModel } from "@app/Models/Models";
import { SelectFilterItem } from '@app/Pages/Configs/Stores/ConfigStores';

export interface IFormCheckboxProps<T extends BaseFormModel> extends Omit<InputProps, 'name'> {
    formModel: T;
    label?: string;
    name: keyof T;
    id?: string;
    onChange?: () => void;
}

@observer
export class FormCheckbox<T extends BaseFormModel> extends React.Component<IFormCheckboxProps<T>, {}> {
    public render() {
        const { label, name, formModel, onChange, disabled } = this.props;
        const value = formModel.getValue(name);
        return (
            <>
                <Label className="checkbox">
                    <Input
                        type="checkbox"
                        checked={value === 'true' || value === true}
                        name={name as string}
                        disabled={disabled}
                        invalid={formModel.validated ? !formModel.isValid(name) : formModel.validated}
                        onChange={ev => {
                            formModel.setValue(name, ev.target.checked);
                            onChange?.();
                        }}
                    />
                    <span>{label}</span>
                </Label>
                <FormFeedback>{formModel.errorFor(name)}</FormFeedback>
            </>
        );
    }
}

@observer
export class FormSwitcher<T extends BaseFormModel> extends React.Component<IFormCheckboxProps<T>, {}> {
    public render() {
        const { name, formModel, changeHandler, label, disabled, title } = this.props;
        if (formModel) {
            const value = formModel.getValue(name as keyof T);
            const tabIndex = disabled ? - 1 : 0;
            return (
                <div className="switcher-wrapper" title={title}>
                    {label && <span className="switcher-label">{label}</span>}
                    <Label className="switcher" tabIndex={tabIndex} onKeyDown={(event) => {
                        if (event.code === KeyboardCodes.ENTER) {
                            formModel.setValue(name as keyof T, !value);
                            if (changeHandler) changeHandler();
                        }
                    }}>
                        <Input
                            type="checkbox"
                            checked={!!value}
                            name={name as string}
                            invalid={formModel.validated ? !formModel.isValid(name as keyof T) : formModel.validated}
                            disabled={disabled}
                            onChange={(ev) => {
                                formModel.setValue(name as keyof T, ev.target.checked);
                                if (changeHandler) changeHandler();
                            }}
                        />
                        <span />
                    </Label>
                    <FormFeedback>{formModel.errorFor(name)}</FormFeedback>
                </div>
            );
        }
    }
}

export interface IFormInputProps<T extends BaseFormModel> extends Omit<InputProps, 'name'> {
    formModel: T;
    name: keyof T;
    formNamePrefix?: string;
    placeholder?: string;
    type?: InputType;
    changeHandler?: Function | void;
    autoComplete?: string;
}

@observer
export class FormInput<T extends BaseFormModel> extends React.Component<IFormInputProps<T>, {}> {
    public render() {
        const { formModel, name, changeHandler, placeholder, bsSize, formNamePrefix, autoComplete, ...rest} = this.props;
        const type: InputType = this.props.type ? this.props.type : 'text';
        const value = formModel.getValue(name);
        return (
            <>
                <Input
                    name={(formNamePrefix ?? '') + (name as string)}
                    value={value as string}
                    invalid={formModel.validated ? !formModel.isValid(name) : formModel.validated}
                    onChange={ev => {
                        formModel.setValue(name, ev.target.value);
                        if (changeHandler) changeHandler(ev);
                    }}
                    placeholder={placeholder}
                    bsSize={bsSize}
                    type={type}
                    autoComplete={autoComplete}
                    {...rest}
                />
                <FormFeedback>{formModel.errorFor(name)}</FormFeedback>
            </>
        );
    }
}

export interface IFormSimpleSelectProps<T extends BaseFormModel> extends Omit<InputProps, 'name'> {
    formModel: T;
    name: keyof T;
    options: string[] | OptionsModel[];
    placeholder?: string;
    hasNoEmptyOption?: boolean;
    onChanged?: Function | void;
}

@observer
export class FormSimpleSelect<T extends BaseFormModel> extends React.Component<IFormSimpleSelectProps<T>, {}> {
    public render() {
        const { formModel, name, onChanged, placeholder, options, hasNoEmptyOption, disabled } = this.props;
        const value = formModel.getValue(name);
        return (
            <>
                <Input
                    name={name as string}
                    value={value as string}
                    invalid={formModel.validated ? !formModel.isValid(name) : formModel.validated}
                    onChange={ev => {
                        formModel.setValue(name, ev.target.value);
                        if (onChanged) onChanged(ev.target.value);}}
                    placeholder={placeholder}
                    disabled={disabled}
                    type="select"
                >
                    {!hasNoEmptyOption && <option value="" />}
                    {options.map((option: string | OptionsModel) => {
                        const value = typeof option === 'string' ? option : option.value;
                        const label = typeof option === 'string' ? option : option.label;
                        return (
                            <option value={value} key={value}>
                                {label}
                            </option>
                        );
                    })}
                </Input>
                <FormFeedback>{formModel.errorFor(name)}</FormFeedback>
            </>
        );
    }
}


export interface IFormSelectProps<T extends BaseFormModel> extends Omit<InputProps, 'name'> {
    formModel: T;
    name: keyof T;
    options: (SelectFilterItem | string)[];
    hasEmpty?: boolean;
    placeholder?: string;
    customOptions?: JSX.Element[];
    changeHandler?: Function;
    customInputChangeHandler?: Function;
    customSelectChangeHandler?: Function;
    showField?: boolean;
    smallValidationError?: boolean;
    disabled?: boolean;
    className?: string;
}

@observer
export class FormSelect<T extends BaseFormModel> extends React.Component<IFormSelectProps<T>> {
    
    constructor (props: IFormSelectProps<T>) {
        super(props);
    }

    public render () {
        const { formModel, name, placeholder, options, customOptions, hasEmpty, changeHandler, smallValidationError, showField, customInputChangeHandler, customSelectChangeHandler, changeYearPeriodHandler, className, ...rest } = this.props;
        const fieldValue = formModel.getValue(name);
        const selectComponent = (
            <>
                <Input
                    name={name as string}
                    className={className}
                    value={fieldValue as unknown as string}
                    invalid={formModel.validated ? !formModel.isValid(name) : formModel.validated}
                    onFocus={(event: React.ChangeEvent<HTMLElement>) => {
                        if (showField && options) {
                            const index = options.findIndex((opt) => {
                                const val = typeof opt === 'string' ? opt : opt.value;
                                return val.toLowerCase() === String(fieldValue).toLowerCase();
                            });
                            const select = event.target as HTMLSelectElement;
                            select.selectedIndex = index;
                        }
                    }}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        formModel.setValue(name, event.target.value);
                        if (changeYearPeriodHandler) changeYearPeriodHandler();
                        if (customSelectChangeHandler) {
                            customSelectChangeHandler();
                        } else if (changeHandler) {
                            changeHandler();
                        }
                    }}
                    placeholder={placeholder}
                    type="select"
                    {...rest}
                >
                    {hasEmpty && <option value="" />}
                    {!customOptions &&
                        options.map((option: SelectFilterItem | string) => {
                            const value = typeof option === 'string' ? option : option.value;
                            const label = typeof option === 'string' ? option : option.label;
                            const className = typeof option === 'string' ? option : option.className || '';
                            return (
                                <option value={value} key={value} className={className}>
                                    {label}
                                </option>
                            );
                        })}
                    {customOptions}
                </Input>
                {formModel.invalidFields.includes(name as string) && (
                    <FormFeedback className={smallValidationError ? 'label-small' : ''}>
                        {formModel.errorFor(name).map((error: string) => (
                            <div key={(name as string) + error}>{error}</div>
                        ))}
                    </FormFeedback>
                )}
            </>
        );
        if (showField) {
            return (
                <div className="select-field">
                    <Input
                        name={name as string}
                        value={fieldValue as unknown as string}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            formModel.setValue(name, event.target.value);
                            if (customInputChangeHandler) {
                                customInputChangeHandler();
                            } else if (changeHandler) {
                                changeHandler();
                            }
                        }}
                        autoComplete="off"
                        {...rest}
                    />
                    {selectComponent}
                </div>
            );
        } else {
            return selectComponent;
        }
    }
}
